import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { HttpClient, HttpParams } from '@angular/common/http';
import { HttpUtilsService } from '../../_base/crud';
import { select, Store } from '@ngrx/store';
import { AppState } from '../../reducers';
import { currentAuthToken, currentUser } from '../../auth';
import { Condition } from '../_models/condition.model';

export enum ViewContext {
  contacts = 'CONTACT',
  patients = 'PATIENT',
}

@Injectable({
  providedIn: 'root',
})
export class ConditionsService {
  private authToken;
  private companyId;

  constructor(
    private http: HttpClient,
    private httpUtils: HttpUtilsService,
    private store: Store<AppState>,
  ) {
    this.store.pipe(select(currentAuthToken)).subscribe((res) => (this.authToken = res));
    this.store.pipe(select(currentUser)).subscribe((res) => (this.companyId = res.companyID));
  }

  findConditions(entityId: number): Observable<any> {
    const headers = this.httpUtils.getHTTPHeaders(this.authToken);
    const params = new HttpParams({ fromString: '_patient=' + entityId + '&_count=' + '&_page=' });
    const url = 'https://aplos2.aidbox.app/Condition';
    return this.http.get<any>(url, { headers, params });
  }

  createCondition(_condition: Condition, entityId) {
    const httpHeaders = this.httpUtils.getHTTPHeaders(this.authToken);
    let onsetValue = null;
    let abatementValue = null;
    switch (_condition.onsetType) {
      case 'dateTime': {
        onsetValue = { dateTime: _condition.onsetDate !== '' ? _condition.onsetDate : null };
        break;
      }
      case 'string': {
        onsetValue = { string: _condition.onsetString !== '' ? _condition.onsetString : null };
        break;
      }
      case 'Period': {
        onsetValue = {
          Period: {
            start: _condition.onsetPeriodStart !== '' ? _condition.onsetPeriodStart : null,
            end: _condition.onsetPeriodEnd !== '' ? _condition.onsetPeriodEnd : null,
          },
        };
        break;
      }
      case 'Age': {
        onsetValue = {
          Age: {
            value: _condition.onsetAge !== '' ? _condition.onsetAge : null,
            unit: _condition.onsetAgeUnit !== '' ? _condition.onsetAgeUnit : null,
          },
        };
        break;
      }
      case 'Range': {
        onsetValue = {
          Range: {
            low: { value: _condition.onsetRangeLow !== '' ? _condition.onsetRangeLow : null },
            high: { value: _condition.onsetRangeHigh !== '' ? _condition.onsetRangeHigh : null },
          },
        };
        break;
      }
      default: {
        break;
      }
    }

    switch (_condition.abatementType) {
      case 'dateTime': {
        abatementValue = {
          dateTime: _condition.abatementDate !== '' ? _condition.abatementDate : null,
        };

        break;
      }
      case 'string': {
        abatementValue = {
          string: _condition.abatementString !== '' ? _condition.abatementString : null,
        };
        break;
      }
      case 'Period': {
        abatementValue = {
          Period: {
            start: _condition.abatementPeriodStart !== '' ? _condition.abatementPeriodStart : null,
            end: _condition.abatementPeriodEnd !== '' ? _condition.abatementPeriodEnd : null,
          },
        };
        break;
      }
      case 'Age': {
        abatementValue = {
          Age: {
            value: _condition.abatementAge !== '' ? _condition.abatementAge : null,
            unit: _condition.abatementAgeUnit !== '' ? _condition.abatementAgeUnit : null,
          },
        };

        break;
      }
      case 'Range': {
        abatementValue = {
          Range: {
            low: {
              value: _condition.abatementRangeLow !== '' ? _condition.abatementRangeLow : null,
            },
            high: {
              value: _condition.abatementRangeHigh !== '' ? _condition.abatementRangeHigh : null,
            },
          },
        };

        break;
      }
      default: {
        break;
      }
    }
    const body = {
      clinicalStatus: {
        coding: [
          {
            code: _condition.clinicalStatus,
            display: _condition.clinicalStatus,
          },
        ],
      },
      verificationStatus: {
        coding: [
          {
            code: _condition.verificationStatus,
            display: _condition.verificationStatus,
          },
        ],
      },
      category: _condition.category,
      severity: {
        coding: [
          {
            code: _condition.severity != null ? _condition.severity[`concept`][`id`] : null,
            display:
              _condition.severity != null ? _condition.severity[`concept`][`fsn`][`term`] : null,
          },
        ],
      },
      bodySite: [
        {
          coding: [
            {
              code: _condition.bodySite != null ? _condition.bodySite[`concept`][`id`] : null,
              display:
                _condition.bodySite != null ? _condition.bodySite[`concept`][`fsn`][`term`] : null,
            },
          ],
        },
      ],
      onset: onsetValue,
      abatement: abatementValue,
      code: {
        coding: [
          {
            code: _condition.code != null ? _condition.code[`concept`][`id`] : null,
            display: _condition.code != null ? _condition.code[`concept`][`fsn`][`term`] : null,
          },
        ],
      },
      subject: {
        id: entityId.toString(),
        resourceType: 'Patient',
      },
      recordedDate: _condition.recordedDate,
      recorder: {
        id: _condition.recorder != null ? _condition.recorder[`id`].toString() : null,
        resourceType:
          _condition.recorderResourceType !== '' && _condition.recorderResourceType !== undefined
            ? _condition.recorderResourceType
            : null,
      },
      asserter: {
        id: _condition.asserter != null ? _condition.asserter[`id`].toString() : null,
        resourceType:
          _condition.asserterType !== '' && _condition.asserterType !== undefined
            ? _condition.asserterType
            : null,
      },
      note: [
        {
          text: _condition.note,
        },
      ],

      stage: [
        {
          summary: {
            coding: [
              {
                code: _condition.summary != null ? _condition.summary[`concept`][`id`] : null,
                display:
                  _condition.summary != null ? _condition.summary[`concept`][`fsn`][`term`] : null,
              },
            ],
          },
          type: {
            coding: [
              {
                code: _condition.type != null ? _condition.type[`concept`][`id`] : null,
                display: _condition.type != null ? _condition.type[`concept`][`fsn`][`term`] : null,
              },
            ],
          },
        },
      ],
      evidence: [
        {
          code: [
            {
              coding: [
                {
                  code: _condition.evidence != null ? _condition.evidence[`concept`][`id`] : null,
                  display:
                    _condition.evidence != null
                      ? _condition.evidence[`concept`][`fsn`][`term`]
                      : null,
                },
              ],
            },
          ],
        },
      ],
    };

    return this.http.post<Condition>('https://aplos2.aidbox.app/Condition', body, {
      headers: httpHeaders,
      observe: 'response',
    });
  }

  updateCondition(_condition: any, entityId): Observable<Condition> {
    const httpHeaders = this.httpUtils.getHTTPHeaders(this.authToken);
    let onsetValue = null;
    let abatementValue = null;
    const url = 'https://aplos2.aidbox.app/Condition/' + _condition.id;

    switch (_condition.onsetType) {
      case 'dateTime': {
        onsetValue = { dateTime: _condition.onsetDate !== '' ? _condition.onsetDate : null };
        break;
      }
      case 'string': {
        onsetValue = { string: _condition.onsetString !== '' ? _condition.onsetString : null };
        break;
      }
      case 'Period': {
        onsetValue = {
          Period: {
            start: _condition.onsetPeriodStart !== '' ? _condition.onsetPeriodStart : null,
            end: _condition.onsetPeriodEnd !== '' ? _condition.onsetPeriodEnd : null,
          },
        };
        break;
      }
      case 'Age': {
        onsetValue = {
          Age: {
            value: _condition.onsetAge !== '' ? _condition.onsetAge : null,
            unit: _condition.onsetAgeUnit !== '' ? _condition.onsetAgeUnit : null,
          },
        };
        break;
      }
      case 'Range': {
        onsetValue = {
          Range: {
            low: { value: _condition.onsetRangeLow !== '' ? _condition.onsetRangeLow : null },
            high: { value: _condition.onsetRangeHigh !== '' ? _condition.onsetRangeHigh : null },
          },
        };
        break;
      }
      default: {
        break;
      }
    }

    switch (_condition.abatementType) {
      case 'dateTime': {
        abatementValue = {
          dateTime: _condition.abatementDate !== '' ? _condition.abatementDate : null,
        };

        break;
      }
      case 'string': {
        abatementValue = {
          string: _condition.abatementString !== '' ? _condition.abatementString : null,
        };
        break;
      }
      case 'Period': {
        abatementValue = {
          Period: {
            start: _condition.abatementPeriodStart !== '' ? _condition.abatementPeriodStart : null,
            end: _condition.abatementPeriodEnd !== '' ? _condition.abatementPeriodEnd : null,
          },
        };
        break;
      }
      case 'Age': {
        abatementValue = {
          Age: {
            value: _condition.abatementAge !== '' ? _condition.abatementAge : null,
            unit: _condition.abatementAgeUnit !== '' ? _condition.abatementAgeUnit : null,
          },
        };

        break;
      }
      case 'Range': {
        abatementValue = {
          Range: {
            low: {
              value: _condition.abatementRangeLow !== '' ? _condition.abatementRangeLow : null,
            },
            high: {
              value: _condition.abatementRangeHigh !== '' ? _condition.abatementRangeHigh : null,
            },
          },
        };

        break;
      }
      default: {
        break;
      }
    }
    const body = {
      clinicalStatus: {
        coding: [
          {
            code: _condition.clinicalStatus,
            display: _condition.clinicalStatus,
          },
        ],
      },
      verificationStatus: {
        coding: [
          {
            code: _condition.verificationStatus,
            display: _condition.verificationStatus,
          },
        ],
      },
      category: _condition.category,
      severity: {
        coding: [
          {
            code: _condition.severity != null ? _condition.severity[`concept`][`id`] : null,
            display:
              _condition.severity != null ? _condition.severity[`concept`][`fsn`][`term`] : null,
          },
        ],
      },
      bodySite: [
        {
          coding: [
            {
              code: _condition.bodySite != null ? _condition.bodySite[`concept`][`id`] : null,
              display:
                _condition.bodySite != null ? _condition.bodySite[`concept`][`fsn`][`term`] : null,
            },
          ],
        },
      ],
      onset: onsetValue,
      abatement: abatementValue,
      code: {
        coding: [
          {
            code: _condition.code != null ? _condition.code[`concept`][`id`] : null,
            display: _condition.code != null ? _condition.code[`concept`][`fsn`][`term`] : null,
          },
        ],
      },
      subject: {
        id: entityId.toString(),
        resourceType: 'Patient',
      },
      recordedDate: _condition.recordedDate,
      recorder: {
        id:
          _condition.recorder != null && _condition.recorder[`id`] !== undefined
            ? _condition.recorder[`id`].toString()
            : null,
        resourceType:
          _condition.recorderResourceType !== '' && _condition.recorderResourceType !== undefined
            ? _condition.recorderResourceType
            : null,
      },
      asserter: {
        id:
          _condition.asserter != null && _condition.asserter[`id`] !== undefined
            ? _condition.asserter[`id`].toString()
            : null,
        resourceType:
          _condition.asserterType !== '' && _condition.asserterType !== undefined
            ? _condition.asserterType
            : null,
      },
      note: [
        {
          text: _condition.note,
        },
      ],

      stage: [
        {
          summary: {
            coding: [
              {
                code: _condition.summary != null ? _condition.summary[`concept`][`id`] : null,
                display:
                  _condition.summary != null ? _condition.summary[`concept`][`fsn`][`term`] : null,
              },
            ],
          },
          type: {
            coding: [
              {
                code: _condition.type != null ? _condition.type[`concept`][`id`] : null,
                display: _condition.type != null ? _condition.type[`concept`][`fsn`][`term`] : null,
              },
            ],
          },
        },
      ],
      evidence: [
        {
          code: [
            {
              coding: [
                {
                  code: _condition.evidence != null ? _condition.evidence[`concept`][`id`] : null,
                  display:
                    _condition.evidence != null
                      ? _condition.evidence[`concept`][`fsn`][`term`]
                      : null,
                },
              ],
            },
          ],
        },
      ],
    };
    return this.http.put<Condition>(url, body, { headers: httpHeaders });
  }

  deleteCondition(id?: number) {
    const httpHeaders = this.httpUtils.getHTTPHeaders(this.authToken);
    return this.http.delete<any>('https://aplos2.aidbox.app/Condition/' + id, {
      headers: httpHeaders,
    });
  }

  getResourceById(ResourceType, resourceId) {
    const headers = this.httpUtils.getHTTPHeaders(this.authToken);
    const url = 'https://aplos2.aidbox.app/' + ResourceType + '/' + resourceId;
    return this.http.get<any>(url, { headers });
  }

  searchConditions(entityId: any, term: string): Observable<any> {
    const params = new HttpParams({
      fromString: '_patient=' + entityId + '&_count=' + '&_page=' + '&code=' + term,
    });
    const headers = this.httpUtils.getHTTPHeaders(this.authToken);
    const url = 'https://aplos2.aidbox.app/Condition';
    return this.http.get<any>(url, { headers, params });
  }
}
